// DNAChat · Painel de Falhas WhatsApp (V4.62)
// - Lista dnachat_messages com status=failed + meta_error estruturado
// - Filtro por retryable / categoria
// - Reprocessa reenvio via /queue/{id}/retry quando aplicável
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, Loader2, ChevronLeft, RotateCw, ShieldAlert, Info } from 'lucide-react';

const fmtDT = (iso) => { try { return new Date(iso).toLocaleString('pt-BR'); } catch { return iso || '—'; } };

const CAT_STYLE = {
  auth:          'bg-rose-50 border-rose-200 text-rose-700',
  rate_limit:    'bg-amber-50 border-amber-200 text-amber-700',
  invalid_input: 'bg-slate-100 border-slate-200 text-slate-700',
  policy:        'bg-purple-50 border-purple-200 text-purple-700',
  window_closed: 'bg-blue-50 border-blue-200 text-blue-700',
  template:      'bg-indigo-50 border-indigo-200 text-indigo-700',
  transient:     'bg-cyan-50 border-cyan-200 text-cyan-700',
  unknown:       'bg-zinc-100 border-zinc-200 text-zinc-600',
};

export default function WhatsAppFailuresPanel() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [filter, setFilter] = useState('all'); // all | retryable | permanent

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/dnachat/whatsapp/failures?limit=100');
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Falha ao carregar falhas');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (filter === 'retryable') return items.filter((i) => i.retryable);
    if (filter === 'permanent') return items.filter((i) => !i.retryable);
    return items;
  }, [items, filter]);

  const retry = async (it) => {
    // O endpoint de retry usa fila; para mensagens fora da fila, isso pode falhar.
    setRetrying(it.id);
    try {
      const { data } = await api.post(`/dnachat/whatsapp/queue/${it.id}/retry`);
      if (data?.success) {
        toast.success(`Reenviado · ID ${String(data.provider_message_id || '').slice(0, 12)}`);
        fetchData();
      } else {
        toast.error(data?.error || 'Falha ao reenviar');
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Item não está na fila (pode ter sido envio direto)');
    } finally { setRetrying(null); }
  };

  const catPill = (cat) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase font-bold ${CAT_STYLE[cat] || CAT_STYLE.unknown}`}>
      {cat || 'unknown'}
    </span>
  );

  const counts = useMemo(() => ({
    retryable: items.filter((i) => i.retryable).length,
    permanent: items.filter((i) => !i.retryable).length,
  }), [items]);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="wa-failures">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <button type="button" onClick={() => navigate('/v2/chat/whatsapp/config')}
            className="text-xs text-slate-500 hover:text-slate-800 mb-1 inline-flex items-center gap-1"
            data-testid="wa-failures-back">
            <ChevronLeft className="w-3.5 h-3.5" /> Voltar para Config
          </button>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-8 h-8 text-rose-600" /> Falhas do WhatsApp
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Mensagens com <b>status=failed</b> — com <code>meta_error_code</code>, causa (Meta) e categoria de retentativa.
            Total no banco: <b>{total}</b>.
          </p>
        </div>
        <button type="button" onClick={fetchData} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold disabled:opacity-50"
          data-testid="wa-failures-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Atualizar
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="wa-failures-tabs">
        {[
          { key: 'all', label: 'Todas', count: items.length },
          { key: 'retryable', label: 'Retentáveis', count: counts.retryable },
          { key: 'permanent', label: 'Permanentes', count: counts.permanent },
        ].map((t) => (
          <button key={t.key} type="button" onClick={() => setFilter(t.key)}
            className={`px-3 py-1.5 rounded-lg border text-sm font-semibold flex items-center gap-2 ${
              filter === t.key ? 'bg-rose-600 border-rose-700 text-white' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
            data-testid={`wa-failures-tab-${t.key}`}>
            {t.label}
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${filter === t.key ? 'bg-white/20 text-white' : 'bg-rose-50 text-rose-700'}`}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="wa-failures-table">
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wider sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Criado</th>
                <th className="text-left px-3 py-2">Responsável</th>
                <th className="text-left px-3 py-2">Prévia</th>
                <th className="text-center px-3 py-2">Código Meta</th>
                <th className="text-center px-3 py-2">Categoria</th>
                <th className="text-left px-3 py-2">Causa</th>
                <th className="text-right px-3 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400 text-sm">
                  Nenhuma falha encontrada com esse filtro. 🎉
                </td></tr>
              )}
              {filtered.map((it) => (
                <tr key={it.id} className="border-t border-slate-100" data-testid={`wa-failures-row-${it.id.slice(0, 8)}`}>
                  <td className="px-3 py-2 text-[11px] text-slate-600 whitespace-nowrap">{fmtDT(it.created_at)}</td>
                  <td className="px-3 py-2">
                    <div className="text-[12px] font-semibold text-slate-800">{it.guardian_name || '—'}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{it.phone || '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-[12px] text-slate-700 max-w-xs truncate" title={it.body || ''}>
                    {it.template_name ? <span className="text-indigo-700 font-mono">template: {it.template_name}</span> : (it.body || '—')}
                  </td>
                  <td className="px-3 py-2 text-center text-[11px] font-mono text-rose-700">
                    {it.meta_error_code ?? '—'}{it.meta_error_subcode ? `/${it.meta_error_subcode}` : ''}
                  </td>
                  <td className="px-3 py-2 text-center">{catPill(it.category)}</td>
                  <td className="px-3 py-2 text-[11px] text-slate-700 max-w-sm">
                    <div className="line-clamp-2" title={it.cause || it.meta_error_message || ''}>
                      {it.cause || it.meta_error_message || '—'}
                    </div>
                    {it.fbtrace_id && <div className="text-[9px] text-slate-400 font-mono mt-0.5">trace: {it.fbtrace_id}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {it.retryable ? (
                      <button type="button" onClick={() => retry(it)} disabled={retrying === it.id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-semibold disabled:opacity-50"
                        data-testid={`wa-failures-retry-${it.id.slice(0, 8)}`}>
                        {retrying === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
                        Reenviar
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-50 border border-slate-200 text-slate-500 text-xs">
                        <ShieldAlert className="w-3.5 h-3.5" /> Permanente
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-500">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          Retentáveis: erros transientes (rate limit, timeout, 5xx). Permanentes: número inválido, política, template rejeitado ou janela de 24h fechada — requerem ação humana ou template aprovado.
        </span>
      </div>
    </div>
  );
}
